import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../../components/FeatherIcon'
import { Spinner } from '../../../components/Spinner'
import { Sentinel } from '../../../library/Sentinel'
import { useMediaSrc } from '../../../library/useMediaSrc'
import { Avatar } from '../../Avatar'
import { Button, Empty, ErrorLine, Modal, Tabs } from '../../settings/kit'
import { useLoad } from '../../settings/kitHooks'
import { errorText, type StreamingCtx } from './context'
import {
  DASHBOARD_REFERER,
  money,
  parsePage,
  parseStats,
  parseViewer,
  pointLabel,
  SERIES,
  seriesPath,
  statsQuery,
  viewersQuery,
  type PastStream,
  type SeriesId,
  type StreamStats,
  type Viewer,
} from './streamingData'

type Tab = 'details' | 'viewers'

/**
 * OnlyFans' STREAM_STAT modal: "Stream statistics" with a settings shortcut,
 * the recording's preview and duration, then DETAILS (viewers, likes,
 * comments and tips over the stream) and VIEWERS.
 */
export function StatsModal({ ctx, stream, onClose }: { ctx: StreamingCtx; stream: PastStream; onClose: () => void }) {
  const { t } = useTranslation('ofMore', { keyPrefix: 'streamingPage' })
  const stats = useLoad(async () => parseStats(await ctx.of.get(`streams/${stream.id}/stats`, statsQuery(), DASHBOARD_REFERER)), [ctx.of, stream.id])
  const [tab, setTab] = useState<Tab>('details')
  const [tippers, setTippers] = useState(false)
  const thumb = useMediaSrc(stream.thumb)

  const openList = (tips: boolean) => {
    setTippers(tips)
    setTab('viewers')
  }

  return (
    <Modal
      onClose={onClose}
      title={
        <span className="ofst-modal-head">
          <span>{t('stats.title')}</span>
          <button
            type="button"
            className="oset-head-btn ofst-modal-gear"
            aria-label={t('stats.settings')}
            title={t('stats.settings')}
            onClick={() => {
              onClose()
              ctx.openPage('settings', 's=streaming')
            }}
          >
            <FeatherIcon name="settings" size={20} />
          </button>
        </span>
      }
      actions={
        <Button variant="flat" onClick={onClose}>
          {t('stats.close')}
        </Button>
      }
    >
      {stats.data ? (
        <div className="ofst-stats">
          <div className="ofst-stats-preview" ref={thumb.ref as React.RefObject<HTMLDivElement>}>
            {thumb.src ? <img src={thumb.src} alt="" /> : <span className="ofst-thumb-ph" aria-hidden="true" />}
            {stats.data.duration && (
              <span className="ofst-duration">
                <FeatherIcon name="radio" size={14} />
                <span>{stats.data.duration}</span>
              </span>
            )}
          </div>
          <Tabs
            label={t('stats.title')}
            value={tab}
            tabs={[
              { id: 'details', label: t('stats.details') },
              { id: 'viewers', label: t('stats.viewers') },
            ]}
            onChange={(next) => {
              if (next === 'viewers') setTippers(false)
              setTab(next)
            }}
          />
          {tab === 'details' ? (
            <StatsChart stats={stats.data} onViewers={() => stats.data!.viewersCount && openList(false)} onTippers={() => stats.data!.tipsCount && openList(true)} />
          ) : (
            <ViewerList key={tippers ? 'tips' : 'viewers'} ctx={ctx} streamId={stream.id} tippers={tippers} />
          )}
        </div>
      ) : stats.loading ? (
        <div className="ofst-center">
          <Spinner size={28} />
        </div>
      ) : (
        <ErrorLine onRetry={stats.reload}>{t('loadFailed', { error: stats.error || '—' })}</ErrorLine>
      )}
    </Modal>
  )
}

const CHART_W = 440
const CHART_H = 152

/** StreamingMultipleChart: four toggleable lines with their totals as the legend. */
function StatsChart({ stats, onViewers, onTippers }: { stats: StreamStats; onViewers: () => void; onTippers: () => void }) {
  const { t } = useTranslation('ofMore', { keyPrefix: 'streamingPage' })
  const [active, setActive] = useState<SeriesId[]>([...SERIES])
  const items: { id: SeriesId; title: string; count: number; sum?: number; open?: () => void }[] = [
    { id: 'views', title: t('stats.viewers'), count: stats.viewersCount, open: onViewers },
    { id: 'likes', title: t('stats.likes'), count: stats.likesCount },
    { id: 'comment', title: t('stats.comments'), count: stats.commentsCount },
    { id: 'tips', title: t('stats.tips'), count: stats.tipsCount, sum: stats.tipSum, open: onTippers },
  ]
  const maxPoint = Math.max(1, ...SERIES.flatMap((id) => stats.series[id].map((p) => p.point)))
  const labels = stats.series.views.length ? [0, maxPoint / 2, maxPoint] : []
  const toggle = (id: SeriesId) => setActive((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]))
  return (
    <div className="ofst-chart">
      <div className="ofst-legend">
        {items.map((it) => (
          <div key={it.id} className={`ofst-legend-item ofst-s-${it.id} ${active.includes(it.id) ? 'is-on' : ''}`}>
            <button type="button" className="ofst-legend-toggle" aria-pressed={active.includes(it.id)} onClick={() => toggle(it.id)}>
              <span className="ofst-legend-dot" aria-hidden="true" />
              <span>{it.title}</span>
            </button>
            {it.open && it.count ? (
              <button type="button" className="ofst-legend-count is-link" onClick={it.open}>
                {it.count}
                {it.sum !== undefined && <span className="ofst-legend-sum"> ({money(it.sum)})</span>}
              </button>
            ) : (
              <span className="ofst-legend-count">
                {it.count}
                {it.sum !== undefined && <span className="ofst-legend-sum"> ({money(it.sum)})</span>}
              </span>
            )}
          </div>
        ))}
      </div>
      <svg className="ofst-chart-svg" viewBox={`0 0 ${CHART_W} ${CHART_H}`} preserveAspectRatio="none" role="img" aria-label={t('stats.details')}>
        {[0.25, 0.5, 0.75].map((f) => (
          <line key={f} className="ofst-chart-grid" x1="0" x2={CHART_W} y1={CHART_H * f} y2={CHART_H * f} vectorEffect="non-scaling-stroke" />
        ))}
        {SERIES.filter((id) => active.includes(id)).map((id) => (
          <path key={id} className={`ofst-chart-line ofst-s-${id}`} d={seriesPath(stats.series[id], maxPoint, CHART_W, CHART_H)} vectorEffect="non-scaling-stroke" />
        ))}
      </svg>
      {labels.length > 0 && (
        <div className="ofst-chart-x" aria-hidden="true">
          {labels.map((s, i) => (
            <span key={i}>{pointLabel(s, maxPoint)}</span>
          ))}
        </div>
      )}
    </div>
  )
}

/** streams/:id/viewers, 8 at a time (with tips only for "Stream tips"). */
function ViewerList({ ctx, streamId, tippers }: { ctx: StreamingCtx; streamId: string; tippers: boolean }) {
  const { t } = useTranslation('ofMore', { keyPrefix: 'streamingPage' })
  const [list, setList] = useState<Viewer[]>([])
  const [hasMore, setHasMore] = useState(true)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const busy = useRef(false)
  const live = useRef(true)
  useEffect(() => {
    live.current = true
    return () => {
      live.current = false
    }
  }, [])

  const more = useCallback(async () => {
    if (busy.current) return
    busy.current = true
    setLoading(true)
    setError('')
    try {
      const offset = list.length
      const page = parsePage(await ctx.of.get(`streams/${streamId}/viewers`, viewersQuery(offset, tippers), DASHBOARD_REFERER), parseViewer)
      if (!live.current) return
      setList((prev) => [...prev, ...page.list.filter((v) => !prev.some((p) => p.id === v.id))])
      setHasMore(page.hasMore && page.list.length > 0)
    } catch (e) {
      if (live.current) setError(errorText(e, t('error')))
    } finally {
      busy.current = false
      if (live.current) setLoading(false)
    }
  }, [ctx.of, streamId, tippers, list.length, t])

  useEffect(() => {
    void more()
    // first page once per list
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="ofst-viewers-list">
      <h4 className="ofst-list-title">{tippers ? t('stats.streamTips') : t('stats.streamViewers')}</h4>
      {list.map((v) => (
        <div key={v.id} className="ofst-viewer">
          <Avatar user={{ name: v.name || v.username, avatar: v.avatar }} size={40} />
          <div className="ofst-viewer-text">
            <span className="ofst-viewer-name">{v.name || v.username}</span>
            {v.username && <span className="ofst-viewer-user">@{v.username}</span>}
          </div>
        </div>
      ))}
      {!loading && !error && !hasMore && list.length === 0 && <Empty>{t('stats.empty')}</Empty>}
      {error && <ErrorLine onRetry={() => void more()}>{t('loadFailed', { error })}</ErrorLine>}
      {loading && (
        <div className="ofst-more">
          <Spinner size={24} />
        </div>
      )}
      {!loading && !error && hasMore && list.length > 0 && <Sentinel onVisible={() => void more()} token={list.length} />}
    </div>
  )
}
